import React from "react";
import { IconWithBackground } from "subframe/components/IconWithBackground";
import { Tag } from "subframe/components/Tag";
import { TagClickLogStatus } from "subframe/components/TagClickLogStatus";
import { CardSysMessage } from "subframe/components/CardSysMessage";
import { IconName } from "@subframe/core";
import { formatLocalDate } from "utils/helper";
import { FeedItem, BadgeVariant, getContactMethodDisplayText } from "modules/Retention/types";

interface ExtendedFeedItem extends Omit<FeedItem, 'type' | 'iconVariant'> {
  type: 'system' | 'action' | 'survey' | 'user_note' | 'system_message';
  iconVariant?: BadgeVariant | string;
  noter_name?: string;
  note_tags?: string[];
  event_type?: string;
  event_subtype?: string;
  metadata?: Record<string, any>;
  actor?: string;
  status?: string;
  sequence?: number;
}

interface TimelineEntryProps {
  item: ExtendedFeedItem;
  isLastItem: boolean;
  getIconVariant: (eventType: string, status: string) => string;
  getIcon: (eventType: string, status: string) => string;
  getStatusVariant: (status: string) => string;
  getStatusDisplayText: (status: string) => string;
}

const TimelineEntry: React.FC<TimelineEntryProps> = ({
  item,
  isLastItem,
  getIconVariant,
  getIcon,
  getStatusVariant,
  getStatusDisplayText,
}) => {
  const isUserNote = item.type === 'user_note' || item.event_type === 'FOLLOWUP_ADDED';
  const isEscalation = item.event_type === 'ESCALATION_CREATED';

  return (
    <div className="flex w-full items-start gap-4">
      {/* Timeline Icon and Line */}
      <div className="flex flex-col items-center self-stretch">
        <div className="flex flex-col items-center">
          <IconWithBackground
            variant={getIconVariant(item.event_type || '', item.status || '') as any}
            size="small"
            icon={getIcon(item.event_type || '', item.status || '') as IconName}
          />
        </div>
        {!isLastItem && (
          <div className="flex w-0.5 grow shrink-0 basis-0 flex-col items-start bg-neutral-border" />
        )}
      </div>

      {/* Content */}
      <div className="flex grow flex-col items-start py-8">
        {isUserNote ? (
          <UserNoteContent
            item={item}
            getStatusVariant={getStatusVariant}
            getStatusDisplayText={getStatusDisplayText}
          />
        ) : isEscalation ? (
          <EscalationContent item={item} />
        ) : (
          <SimpleContent item={item} />
        )}
      </div>
    </div>
  );
};

const UserNoteContent: React.FC<{
  item: ExtendedFeedItem;
  getStatusVariant: (status: string) => string;
  getStatusDisplayText: (status: string) => string;
}> = ({ item, getStatusVariant, getStatusDisplayText }) => (
  <div className="flex flex-col gap-2 w-full -mt-8">
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-1">
        <span className="text-body-bold font-body-bold text-default-font">
          {(item.noter_name === 'System' || item.actor === 'System') ? 'Admin' : (item.noter_name || item.actor || 'Admin')}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {item.metadata?.contact_method && (
          <>
            <span className="text-label font-label text-subtext-color">
              {getContactMethodDisplayText(item.metadata.contact_method)}
            </span>
            <div className="flex h-3.5 w-0.5 bg-neutral-400" />
          </>
        )}
        <span className="text-label font-label text-subtext-color">
          {formatLocalDate(item.metadata?.contact_date || item.date)}
        </span>
      </div>
    </div>
    <div className="flex flex-col gap-4 rounded-lg border border-solid border-neutral-border bg-white px-4 py-4">
      <div className="flex flex-col gap-2">
        {item.note_tags && item.note_tags.length > 0 && (
          <div className="flex items-start gap-2">
            {item.note_tags.map((tag: string, tagIdx: number) => (
              <Tag key={tagIdx} variant="blue" score={tag} />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 rounded-md px-2 py-1">
          <span className="text-body-16 font-body-16 text-neutral-600">
            {item.metadata?.notes || item.title}
          </span>
        </div>
        <TagClickLogStatus
          icon="FeatherHourglass"
          variant={getStatusVariant(item.status || '') as any}
          text={getStatusDisplayText(item.status || '')}
        />
      </div>
    </div>
  </div>
);

const EscalationContent: React.FC<{ item: ExtendedFeedItem }> = ({ item }) => (
  <div className="flex flex-col gap-2 rounded-lg bg-white pb-2 w-full -mt-8">
    <div className="flex w-full items-center justify-between">
      <CardSysMessage mainContent={item.title} dateTime="" />
      <span className="text-label font-label text-subtext-color">
        {formatLocalDate(item.date)}
      </span>
    </div>
  </div>
);

const SimpleContent: React.FC<{ item: ExtendedFeedItem }> = ({ item }) => (
  <div className="flex items-center justify-between min-h-[1.5rem] w-full -mt-8">
    <div className="flex items-center">
      <span className="text-body-bold font-body-bold text-default-font">
        {item.title}
      </span>
    </div>
    <span className="text-label font-label text-subtext-color">
      {formatLocalDate(item.date)}
    </span>
  </div>
);

export default TimelineEntry; 